import React, { useState } from "react";
import {
  Box,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchBar from "./SearchBar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import RunningIcon from "./RunningIcon";
import {
  faBoxesPacking,
  faCheck,
  faGlobe,
  faPlus,
  faFileAlt,
  faChartLine,
  faCompactDisc,
  faBuildingUser,
  faLaptopCode,
  faCog,
  faArchive,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import AddCompanyForm from "../Companies/AddCompanyForm";
import ApplicationFormModal from "../Applications/ApplicationFormModal";
import useAdminStatus from "../../Hooks/useAdminStatus";
import DownloadDialog from "../DownloadDialog"

function PageTitle({
  title,
  placeholderText,
  onSearchChange,
  filter,
  handleFilterChange,
  completedDeploymentsCount,
  archiveCompletedDeployments,
  showArchive,
  setArchive,
  iconName,
  showFilters = false,
  showAddButton = false,
  showDownloadButton, // New prop to show download button
  menuVisible,
  pageType = "applications", // New prop to indicate page type
}) {
  const { isAdmin } = useAdminStatus();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery("(max-width:850px)");
  const isSmallScreenHeight = useMediaQuery("(max-width:1300px)");
  const [openAppModal, setOpenAppModal] = useState(false);
  const [openCompanyModal, setOpenCompanyModal] = useState(false);

  // Handle Add Button Click Based on Page Type
  const handleOpen = () => {
    if (pageType === "applications") {
      if (isAdmin) {
        setOpenAppModal(true); // Open Application Modal
      }
    } else if (pageType === "companies") {
      setOpenCompanyModal(true); // Open Company Modal
    }
  };

  const handleCloseAppModal = () => setOpenAppModal(false);
  const handleCloseCompanyModal = () => setOpenCompanyModal(false);


 // Icon mapping function
const getIconByName = (name) => {
  switch (name.toLowerCase()) {
    case "deployments":
      return faLaptopCode;
    case "archives":
      return faArchive;
    case "apps":
      return faCompactDisc;
    case "companies":
      return faBuildingUser;
    case "logs":
      return faFileAlt;
    case "statistics":
      return faChartLine;
    case "settings":
      return faCog;
    case "faq":
      return faQuestionCircle;
    default:
      return faQuestionCircle; // Default icon in case of a missing match
  }
};

  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: isSmallScreenHeight ? "100%" : "60px",
        display: "flex",
        flexDirection: isSmallScreen ? "column" : "row",
        justifyContent: "space-between",
        alignItems: isSmallScreen ? "flex-start" : "center",
        padding: "10px 20px",
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.text,
        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
        marginBottom: "16px",
        position: "sticky",
        top: menuVisible ? (isSmallScreen ? "120px" : "160px") : "80px",
        zIndex: 1,
        transition: "top 0.3s ease-in-out",
      }}
    >
      {/* Title with Icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center", // Align icon and text vertically
        }}
      >
        {/* Icon */}
        <FontAwesomeIcon
          icon={getIconByName(iconName)} // Pass the icon variable here
          style={{
            marginRight: "8px", // Add some space between the icon and title
            fontSize: isSmallScreen ? "1.2rem" : "1.5rem", // Adjust icon size based on screen
          }}
        />

        {/* Title */}
        <Typography
          variant="h6"
          component="h1"
          sx={{
            fontWeight: "bold",
            marginBottom: isSmallScreen ? "10px" : 0,
            fontSize: isSmallScreen ? "1.1rem" : "1.5rem",
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Search Bar and Buttons */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "flex-end",
          width: isSmallScreen ? "100%" : "auto",
        }}
      >
        {/* Search Bar */}
        <Box
          sx={{
            marginRight: isSmallScreen ? 0 : 2,
            marginBottom: isSmallScreen ? "10px" : 0,
            width: isSmallScreen ? "100%" : "500px",
          }}
        >
          <SearchBar
            onChange={onSearchChange}
            placeholderText={placeholderText}
          />
        </Box>

        {/* Filter Buttons */}
        {showFilters && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: isSmallScreen ? "10px" : 0,
            }}
          >
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={(e, newFilter) => handleFilterChange(newFilter)}
              sx={{
                marginLeft: isSmallScreen ? 0 : "16px",
              }}
            >
              <Tooltip arrow title="Show All Deployments">
                <ToggleButton
                  value="all"
                  sx={{
                    backgroundColor: "transparent",
                    border: "none",
                    padding: "8px",
                    fontSize: isSmallScreen ? "1.2rem" : "1.5rem",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faGlobe}
                    style={{
                      color:
                        filter === "all"
                          ? theme.palette.secondary.main
                          : theme.palette.info.main,
                    }}
                  />
                </ToggleButton>
              </Tooltip>

              <Tooltip arrow title="Show Running Deployments">
                <ToggleButton
                  value="running"
                  sx={{
                    backgroundColor: "transparent",
                    border: "none",
                    padding: "8px",
                    fontSize: isSmallScreen ? "1.2rem" : "1.5rem",
                  }}
                >
                  <RunningIcon isActive={filter === "running"} />
                </ToggleButton>
              </Tooltip>

              <Tooltip arrow title="Show Completed Deployments">
                <ToggleButton
                  value="complete"
                  sx={{
                    backgroundColor: "transparent",
                    border: "none",
                    padding: "8px",
                    fontSize: isSmallScreen ? "1.2rem" : "1.5rem",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCheck}
                    style={{
                      color:
                        filter === "complete"
                          ? theme.palette.secondary.main
                          : theme.palette.info.main,
                    }}
                  />
                </ToggleButton>
              </Tooltip>
            </ToggleButtonGroup>

            {/* Archive Button */}
            {completedDeploymentsCount > 0 && (
              <Tooltip arrow title="Archive All Completed Deployments">
                <IconButton
                  onClick={archiveCompletedDeployments}
                  sx={{
                    color: theme.palette.secondary.main,
                    padding: "8px",
                    fontSize: isSmallScreen ? "1.2rem" : "1.5rem",
                  }}
                >
                  <FontAwesomeIcon icon={faBoxesPacking} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {/* Add Button */}
        {showAddButton && (
          <IconButton
            onClick={handleOpen}
            sx={{
              color: theme.palette.success.text,
              marginRight: showDownloadButton ? "8px" : "16px", // Adjust spacing if download button is shown
              fontSize: isSmallScreen ? "1.2rem" : "1.5rem",
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
          </IconButton>
        )}

        {/* Download Button */}
        {showDownloadButton && (
          <DownloadDialog showDownloadButton={showDownloadButton} />
        )}
      </Box>

      {/* Application Form Modal */}
      <ApplicationFormModal
        open={openAppModal}
        onClose={handleCloseAppModal}
        fetchApplications={() => {}}
      />

      {/* Add Company Form Modal */}
      <AddCompanyForm
        open={openCompanyModal}
        onClose={handleCloseCompanyModal}
      />
    </Box>
  );
}

export default PageTitle;
